// PhoneInput — composite control per design: [country badge + dial-code picker]
// | divider | digits-only tel input (max 12 digits) | phone glyph. The shell is forced LTR
// so the dial code / number order and digit flow never flip under RTL.
//
// The dial picker is built on the `Dropdown` primitive (SortDropdown's
// menuitemradio pattern): this component owns the trigger and the rows, and
// this shell draws the border and focus ring. The panel is portaled because
// three call sites sit inside Drawers whose body clips overflow.

import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import type { Country } from '@/mocks/countries';
import Dropdown from './Dropdown';
import Icon from './Icon';

/** National-number cap — applied after the digit strip, so pastes count digits only. */
const MAX_DIGITS = 12;

/** Portaled panels need a px width (an unmeasured portal renders at the 200px fallback). */
const DIAL_PANEL_WIDTH = 140;

export interface PhoneInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  countries: Country[];
  /** Selected dial code, e.g. '+966'. */
  dialCode: string;
  onDialCodeChange: (dial: string) => void;
  /** National number, digits only. */
  value: string;
  onChange: (digits: string) => void;
  /** Render the error (red) border. */
  error?: boolean;
  /** Hide the colored country-code badge to keep the dial section compact. */
  hideBadge?: boolean;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    countries,
    dialCode,
    onDialCodeChange,
    value,
    onChange,
    error = false,
    hideBadge = false,
    readOnly = false,
    className = '',
    ...rest
  },
  ref,
) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = countries.find((c) => c.dial === dialCode) ?? countries[0];
  const classes = [
    'app-phone-input',
    error && 'app-phone-input--error',
    readOnly && 'app-phone-input--readonly',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const pick = (dial: string) => {
    onDialCodeChange(dial);
    setOpen(false);
  };

  return (
    // div, not span: Dropdown renders a <div> wrapper, which must not nest in a span.
    <div className={classes} dir='ltr'>
      <div className='app-phone-input__dial'>
        {!hideBadge && (
          <span
            className='app-phone-input__badge'
            style={{ backgroundColor: selected.flagColor }}
            aria-hidden='true'
          >
            {selected.code}
          </span>
        )}
        <Dropdown
          open={open}
          onClose={() => setOpen(false)}
          align='start'
          width={DIAL_PANEL_WIDTH}
          portal
          flip
          className='app-phone-input__dial-select'
          trigger={
            <button
              type='button'
              className={`app-phone-input__dial-trigger${open ? ' is-open' : ''}`}
              aria-haspopup='menu'
              aria-expanded={open}
              aria-label={t('aria_phone_dial_code')}
              disabled={readOnly}
              onClick={() => setOpen((o) => !o)}
            >
              {selected.dial}
              <Icon
                name={open ? 'chevron-up' : 'chevron-down'}
                size={14}
                className='app-phone-input__chevron'
                aria-hidden='true'
              />
            </button>
          }
        >
          {countries.map((c) => {
            const active = c.dial === selected.dial;
            return (
              <button
                key={c.iso}
                type='button'
                className='app-dropdown__item'
                role='menuitemradio'
                aria-checked={active}
                onClick={() => pick(c.dial)}
              >
                <span className='app-phone-input__dial-label'>{c.dial}</span>
                {active && (
                  <Icon
                    name='check'
                    size={16}
                    className='app-phone-input__dial-check'
                    aria-hidden='true'
                  />
                )}
              </button>
            );
          })}
        </Dropdown>
      </div>
      <input
        ref={ref}
        type='tel'
        inputMode='tel'
        autoComplete='tel-national'
        className='app-phone-input__control'
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, MAX_DIGITS))}
        readOnly={readOnly}
        aria-invalid={error || undefined}
        {...rest}
      />
      <Icon name='phone' size={18} className='app-phone-input__icon' aria-hidden='true' />
    </div>
  );
});

export default PhoneInput;
